import { apiQueryKey, getQuerySession, queryClient } from "@/api/query-client";
import { Preferences } from "@Shared/enums";
import { Api } from "@Shared/schemas";
import { UpdatePreferencesRequest, UpdatePreferencesResponse, UserApis, UserPreferences } from "@Shared/schemas/users/userSchemas";
import { mutationOptions } from "@tanstack/react-query";
import { getAuth } from "firebase/auth";

export const preferencesRequest = { action: "REQUEST" } as const;
export const preferencesQueryKey = (userId: string) => apiQueryKey(UserApis.Users.GetPreferences.Name, userId, [], preferencesRequest);

function assertCurrentUser(userId: string) {
  if (!userId || getAuth().currentUser?.uid !== userId) throw new Error("Preferences require the current signed-in user");
}

// Lifecycle options for the preferences mutation hook; no transport or dispatch adapter.
export const preferencesMutationOptions = (userId: string, disableLoader = true) => {
  const session = getQuerySession();
  return mutationOptions<UpdatePreferencesResponse, Error, UpdatePreferencesRequest, { session: number }>({
    mutationKey: [UserApis.Users.UpdatePreferences.Name, userId, "update"],
    scope: { id: `preferences:${userId}` },
    retry: false,
    meta: { apiName: UserApis.Users.UpdatePreferences.Name, disableLoader },
    onMutate: async () => {
      assertCurrentUser(userId);
      return { session };
    },
    onSuccess: async (_response, { preferences }, context) => {
      if (context?.session !== getQuerySession() || getAuth().currentUser?.uid !== userId) return;
      await queryClient.cancelQueries({ queryKey: preferencesQueryKey(userId) });
      if (context.session !== getQuerySession()) return;
      // The update endpoint returns an acknowledgement, not a complete preferences object.
      // Never seed the full-data cache with only the fields from a partial update.
      queryClient.setQueryData<UserPreferences>(preferencesQueryKey(userId), current =>
        current ? { ...current, ...Object.fromEntries(preferences.map(({ id, value }) => [id, value])) } : undefined
      );
      if (preferences.some(({ id }) => id === Preferences.leaderboard_visibility || id === Preferences.leaderboard_anonymity)) {
        void queryClient.invalidateQueries({ queryKey: [Api.Stats.GetLeaderboard.Name] });
      }
      if (!queryClient.getQueryData(preferencesQueryKey(userId))) {
        await queryClient.invalidateQueries({ queryKey: preferencesQueryKey(userId) });
      }
    },
  });
};
